'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { clsx } from 'clsx';

interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
  ariaLabel: string;
  panelClassName?: string;
  showCloseButton?: boolean;
}

interface ModalCloseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
}

let bodyScrollLockCount = 0;
let previousBodyOverflow = '';

const lockBodyScroll = () => {
  if (bodyScrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyScrollLockCount += 1;
};

const unlockBodyScroll = () => {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
  }
};

export function ModalCloseButton({
  label = '닫기',
  className,
  ...props
}: ModalCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={clsx(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100',
        className,
      )}
      {...props}
    >
      <FiX className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

export default function Modal({
  children,
  onClose,
  ariaLabel,
  panelClassName,
  showCloseButton = true,
}: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    lockBodyScroll();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      unlockBodyScroll();
    };
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto p-4 sm:p-6">
      <button
        type="button"
        tabIndex={-1}
        aria-label={`${ariaLabel} 닫기`}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm transition-opacity dark:bg-black/65"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={clsx(
          'admin-modal relative z-10 border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100',
          panelClassName,
        )}
      >
        {showCloseButton && (
          <ModalCloseButton
            onClick={onClose}
            className="absolute right-4 top-4 z-20"
          />
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
